import React, {useEffect, useRef, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import _cloneDeep from 'lodash/cloneDeep';
import {Link} from 'react-router-dom';
import ManualDataTable from '../../../../../components/manualdatatable/ManualDatatable';
import {
  DateRangeFilter,
  DropdownFilter,
  TextFilter,
} from '../../../../../components/manualdatatable/ManualDataFilters';
import FormattedDate from '../../../../../components/product/FormattedDate';
import ActionButton from '../../../../../components/datatable/ActionButton';
import skincareProDao from '../../../../../dao/skincareProDao';
import {getCategory, mapCategories} from '../../../../../utils/skincareUtils';
import AddSkincare from '../../addskincarehead';
import _get from 'lodash/get';
import './index.css';
import _isEmpty from 'lodash/isEmpty';
import InfoDialog from '../../../../../components/dialogs/InfoDialog';
import smbUtils from '../../../../../utils/smbUtils';
import {history} from '../../../../../utils/redux/store';
import {multipleSubscriptionsUtils} from '../../../../../utils/solutionUtils';
import {privilegeUtils} from '../../../../../utils/privilegeUtils';
import ActionDropdownButton from '../../../../../components/btngroup/ActionDropdownButton';
import AddOrEditBatchImportDialog from '../../components/AddOrEditBatchImportDialog';

const ControlBar = ({
  isQuotaFull = false,
  onOpenBatchImport,
  isCategoryLoading = false,
}) => {
  const {t} = useTranslation();
  const config = {
    main: {
      label: (
        <>
          <i className="fa fa-file" />
          <Trans i18nKey="p.s.addskincare.batch.import">
            Batch Import
          </Trans>
        </>
      ),
    },
    dropdown: [
      {
        label: (
          <>
            <i className="fa fa-plus" />
            <Trans i18nKey="p.s.addskincare.batch.import.add.product">
              Add Product
            </Trans>
          </>
        ),
        onClick: () => onOpenBatchImport('add'),
      },
      {
        label: (
          <>
            <i className="fa fa-pencil-alt" />
            <Trans i18nKey="p.s.addskincare.batch.import.edit.product">
              Edit Product
            </Trans>
          </>
        ),
        onClick: () => onOpenBatchImport('edit'),
      },
    ],
  };
  return (
    <div className="control-bar">
      <ActionButton
        type={'customize'}
        disabled={isQuotaFull}
        disableDesc={
          <Trans i18nKey="p.s.listskincareproduct.add.skincareproduct.quota.full">
            Cannot add more{' '}
            {{
              product: t('p.s.listskincareproduct.product', 'product'),
            }}{' '}
            as the total quota has been reached.
          </Trans>
        }
        customize={{
          text: (
            <>
              <i className="fa fa-plus" />
              <Trans i18nKey="p.s.listskincareproduct.add.skincareproduct">
                Add Skincare Product
              </Trans>
            </>
          ),
        }}
        onClick={() =>
          history.push('/skincare-pro/add-skincare/add-skincare-product')
        }
      />
      <ActionDropdownButton
        config={config}
        className="btn-group kt-margin-b-10"
        disabled={isCategoryLoading}
      />
      <Link
        to="/skincare-pro/add-skincare/list-product-category"
        className="btn btn-success kt-margin-b-10"
      >
        <i className="fa fa-cog" />
        <Trans i18nKey="p.s.listskincareproduct.category.management">
          Category Management
        </Trans>
      </Link>
    </div>
  );
};

export default function SkincareProduct() {
  const accountSummary = useSelector(
    state => state.components.auth.accountSummary
  );
  const {token, customerId} = smbUtils.customerInfo(accountSummary);
  const {SKIN_ANALYSIS} = multipleSubscriptionsUtils.getSolutionInfos(
    accountSummary
  );
  const productCountTotal = _get(SKIN_ANALYSIS, 'maxNumOfProducts', 0);
  const brandName = smbUtils.getCustomerName(accountSummary);
  const [formattedData, setFormattedData] = useState({
    results: [],
    totalSize: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [productCount, setProductCount] = useState(0);
  const [processedParams, setProcessedParams] = useState(null);
  const [categoryMapping, setCategoryMapping] = useState({});
  const [categoryList, setCategoryList] = useState([]);
  const [isCategoryLoading, setIsCategoryLoading] = useState(true);
  const [showBatchImportDialog, setShowBatchImportDialog] = useState(false);
  const [batchImportAction, setBatchImportAction] = useState('add');
  const isQuotaFull =
    productCountTotal === -1 ? false : productCount >= productCountTotal;
  let callPaginationAPI = useRef(null);

  const getProductCountAPI = async () => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    await skincareProDao.getProductCount(tmp).then(res => {
      if (res.status === 'success' && res.count) {
        setProductCount(res.count);
      }
    });
  };

  const listProductAPI = async () => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append(
      'types',
      JSON.stringify([{type1: 'Store', type2: 'SMB', type3: 'SMB_PRODUCT'}])
    );
    tmp.append('service', 'INVENTORY');

    const apiFormattedParams = _cloneDeep(processedParams);
    tmp.append('limit', apiFormattedParams.pageSize);
    tmp.append(
      'offset',
      (apiFormattedParams.pageIndex - 1) * apiFormattedParams.pageSize
    );
    delete apiFormattedParams.pageSize;
    delete apiFormattedParams.pageIndex;

    for (const [key, value] of Object.entries(apiFormattedParams)) {
      if (key === 'status' && value === 'Offline') {
        tmp.append(key, JSON.stringify(['Active', 'Offline']));
        continue;
      }
      if (
        key === 'createdTimeFrom' ||
        key === 'createdTimeTo' ||
        key === 'lastModifiedFrom' ||
        key === 'lastModifiedTo'
      ) {
        tmp.append(key, new Date(value).toISOString());
        continue;
      }
      tmp.append(key, value);
    }

    await skincareProDao
      .getGenericList(tmp)
      .then(res => {
        if (res.results) {
          const originalData = res.results.map(item => {
            return {
              id: item.id,
              deletable: item.deletable,
              imageUrl: item.editingRevision.imageUrl,
              categoryId: item.categoryIds[0],
              status: item.editingRevision.status,
              createdTime: item.createdTime,
              lastModified: item.lastModified,
              name: item.editingRevision.name,
              i18n: item.editingRevision.i18n,
            };
          });

          const getI18nData = i18nData => {
            const i18nKeys = Object.keys(i18nData);

            const defaultKey = i18nKeys.find(key => i18nData[key].isDefault);
            return i18nData[defaultKey];
          };

          const newRes = originalData.map(item => {
            const i18nData = getI18nData(item.i18n);
            return {
              id: item.id,
              deletable: item.deletable,
              imageUrl: item.imageUrl,
              categoryId: item.categoryId,
              status: item.status,
              createdTime: item.createdTime,
              lastModified: item.lastModified,
              name: item.name,
              actionUrl: i18nData.actionUrl,
            };
          });
          setFormattedData({
            ...formattedData,
            results: newRes,
            totalSize: _get(res, 'totalSize', 0),
          });
          setIsLoading(false);
          return;
        }
      })
      .catch(err => {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
      });
    setIsLoading(false);
  };
  const copyProductAPI = async productId => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('productId', productId);
    const res = await skincareProDao.copyGenericList(tmp);
    if (!res || res.status !== 'success') {
      toast.error(
        <Trans i18nKey="p.s.addoreditcategory.error.failed">Failed</Trans>
      );
      return;
    }
    await getProductCountAPI();
    await listProductAPI();
    toast.success(<Trans i18nKey="toast.success.copy.product">Copied!</Trans>);
    setIsLoading(false);
  };
  const deleteProductAPI = async (productId, productName) => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('productId', productId);
    const res = await skincareProDao.deleteGenericList(tmp);
    if (!res || res.status !== 'success') {
      toast.error(
        <Trans i18nKey="toast.error.delete.product">
          Failed to delete {{productName}}.
        </Trans>
      );
      return;
    }
    await getProductCountAPI();
    await listProductAPI();
    toast.success(
      <Trans i18nKey="toast.success.delete.product">Delete!</Trans>
    );
    setIsLoading(false);
  };

  useEffect(() => {
    setIsCategoryLoading(true);
    getCategory(token, customerId, 'Skincare').then(resCategory => {
      setCategoryList(resCategory);
      setCategoryMapping(mapCategories(resCategory));
      setIsCategoryLoading(false);
    });
    getProductCountAPI();
  }, []);

  useEffect(() => {
    if (!processedParams) {
      return;
    }
    setIsLoading(true);
    callPaginationAPI.current = smbUtils.clearPrevAPI(
      callPaginationAPI.current,
      listProductAPI,
      500
    );
  }, [processedParams]);

  const columns = [
    {
      Header: <Trans i18nKey="p.s.listskincareproduct.image">Image</Trans>,
      accessor: 'imageUrl',
      disableSortBy: true,
      style: {width: '10%'},
      Cell: ({cell: {value}}) => (
        <img
          alt=""
          src={value}
          style={{height: '50px', display: 'block', margin: '0 auto'}}
        />
      ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.listskincareproduct.category">Category</Trans>
      ),
      accessor: 'categoryId',
      disableSortBy: true,
      Filter: DropdownFilter,
      options: categoryList,
      style: {width: '10%', textAlign: 'center'},
      Cell: ({cell: {value}}) =>
        Object.keys(categoryMapping).length !== 0 ? (
          categoryMapping[value]
        ) : (
          <>{value}</>
        ),
    },
    {
      Header: (
        <InfoDialog
          trigger={'click'}
          placement="right"
          title={
            <>
              <i className="fa fa-question-circle" />{' '}
              <Trans i18nKey="p.s.listskincareproduct.product.Name">
                Product Name
              </Trans>
            </>
          }
          description={
            <Trans i18nKey="p.s.listskincareproduct.product.Name.desc">
              Names are displayed in default language.
            </Trans>
          }
        >
          <label className="cursor-help">
            <Trans i18nKey="p.s.listskincareproduct.product.Name">
              Product Name
            </Trans>{' '}
            <i className="fa fa-question-circle" />
          </label>
        </InfoDialog>
      ),
      disableSortBy: true,
      accessor: 'name',
      style: {width: '10%', textAlign: 'center', wordBreak: 'break-word'},
      Filter: TextFilter,
    },
    {
      Header: (
        <Trans i18nKey="p.s.listskincareproduct.product.id">Product ID</Trans>
      ),
      accessor: 'id',
      disableSortBy: true,
      style: {width: '10%', textAlign: 'center'},
      Filter: TextFilter,
    },
    {
      Header: (
        <Trans i18nKey="p.s.listskincareproduct.action.url">Action Url</Trans>
      ),
      accessor: 'actionUrl',
      disableSortBy: true,
      style: {width: '10%', textAlign: 'center'},
      Cell: ({cell: {value}}) =>
        !_isEmpty(value) ? (
          <div className="text-center kt-padding-10">
            <a
              href={value}
              target="_blank"
              className="cursor-pointer text-primary"
              style={{fontSize: '18px'}}
            >
              <i className="flaticon2-link" />
            </a>
          </div>
        ) : (
          <></>
        ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.listskincareproduct.creation">Creation</Trans>
      ),
      accessor: 'createdTime',
      Filter: DateRangeFilter,
      style: {width: '12.5%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div className="text-right">
          <FormattedDate time={value} type={'long'} />
        </div>
      ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.listskincareproduct.modification">
          Modification
        </Trans>
      ),
      accessor: 'lastModified',
      style: {width: '12.5%', textAlign: 'center'},
      Filter: DateRangeFilter,
      Cell: ({cell: {value}}) => (
        <div className="text-right">
          <FormattedDate time={value} type={'long'} />
        </div>
      ),
    },
    {
      Header: <Trans i18nKey="p.s.listskincareproduct.actions">Actions</Trans>,
      accessor: 'id',
      disableSortBy: true,
      id: 'actions',
      style: {width: '14%'},
      Cell: ({
        cell: {
          row: {original},
        },
      }) => (
        <div
          className="list-skincare-product-action-button"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ActionButton
            type={'edit'}
            url={'/skincare-pro/add-skincare/edit-skincare-product'}
            query={{productId: original.id}}
          />
          <ActionButton
            type={'copy'}
            onClick={async () => {
              await copyProductAPI(original.id);
            }}
            disabled={isQuotaFull}
            disableDesc={
              <Trans i18nKey="p.s.listskincareproduct.disable.copy.desc">
                The Product cannot be duplicated as the maximum amount has been
                reached. Please upgrade your plan or remove existing product to
                proceed.
              </Trans>
            }
          />
          <ActionButton
            type={'delete'}
            onClick={async () => {
              await deleteProductAPI(original.id, original.name);
            }}
            disabled={!original.deletable}
            disableDesc={
              <Trans i18nKey="p.s.listskincareproduct.disable.delete.desc">
                This Product is being used in Routines or Recommendation Cases.
                If you wish to delete this product, please remove it from
                Routines or Recommendation Cases first.
              </Trans>
            }
            confirmTitle={
              <Trans i18nKey="p.s.listskincareproduct.confirm.delete.desc">
                After deletion, the product will no longer be available on past
                client reports. Are you sure to delete?
              </Trans>
            }
          />
        </div>
      ),
    },
  ];

  if (
    !(
      privilegeUtils.canManageSku(accountSummary) ||
      privilegeUtils.isTechnicalSupport(accountSummary)
    )
  ) {
    history.push('/');
  }

  return (
    <>
      <AddSkincare
        numOfEnabled={productCount}
        numOfAvailable={productCountTotal}
      />
      <div className="portlet-body">
        <ControlBar
          isQuotaFull={isQuotaFull}
          isCategoryLoading={isCategoryLoading}
          onOpenBatchImport={action => {
            setBatchImportAction(action);
            setShowBatchImportDialog(true);
          }}
        />
        <div className="list-skincare-product-description">
          <img
            alt="guid"
            src={require('../../../../../assets/images/help/help_start_guide.svg')}
            className="kt-margin-b-5 kt-margin-r-5"
          />{' '}
          <Trans i18nKey="p.s.listskincareproduct.desc">
            Skincare Products are used for maintaining the health condition of
            your skin.
          </Trans>
        </div>
        <div className="list-skincare-product-page">
          <ManualDataTable
            isLoading={isLoading}
            columns={columns}
            data={formattedData.results}
            totalSize={formattedData.totalSize}
            initState={{
              sortBy: {id: 'lastModified', desc: true},
              pageSize: 10,
              pageIndex: 1,
            }}
            setProcessedParams={setProcessedParams}
            isScrollX={true}
          />
        </div>
      </div>
      <AddOrEditBatchImportDialog
        show={showBatchImportDialog}
        setShow={setShowBatchImportDialog}
        action={batchImportAction}
        type="product"
        categories={categoryList}
        productCount={productCount}
        totalQuota={productCountTotal === -1 ? Number.MAX_SAFE_INTEGER : productCountTotal}
        brandName={brandName}
        token={token}
        customerId={customerId}
        processedParams={processedParams}
        refetch={async () => {
          await getProductCountAPI();
          await listProductAPI();
          setIsLoading(false);
        }}
      />
    </>
  );
}
